import type { DisplayNameParts } from "../components/profile-ui";
import { displayName } from "../components/profile-ui";
import { fa } from "../i18n/fa";
import type { LobbyPlayer } from "./online-connection";

/**
 * How a table turns a SEAT into a person.
 *
 * Every online table faces the same three-step question — is this seat me, a known human, or
 * nobody in particular? — and حکم and بی‌دل had each grown their own copy of the answer. شلم,
 * پاسور and بلوف had none at all: their containers passed no roster, so a human friend sat there
 * as «بات N» with a positional stand-in face and no verified badge, however official they were.
 *
 * The resolution is shared here; only the two things that legitimately DIFFER per game are passed
 * in — the word for your own seat, and how that game numbers the anonymous ones (حکم has a
 * partner opposite you, بی‌دل ranks bots by seat, the rest number by absolute seat). Sharing the
 * resolution and injecting the difference is what keeps five tables from drifting apart again.
 */

/** The public, leak-free identity of a person at a table (never a Telegram id). */
export interface SeatIdentity extends DisplayNameParts {
  /** Their chosen PRESET avatar id (`"fox"`…). */
  avatarId?: string | null;
  /** Their UPLOADED photo id — `GET /avatar/:id`, rendered by `Avatar`. */
  avatarPhotoId?: string | null;
  /** Official/verified account → the disc wears the badge. */
  isOfficial?: boolean;
}

/** A seated occupant as the lobby roster reports it. A bot is flagged so it keeps a positional
 *  label instead of borrowing a name. */
export interface SeatPlayer extends SeatIdentity {
  seat: number;
  isBot?: boolean;
}

/** Exactly what `Avatar` needs to draw a seat. */
export interface SeatAvatar {
  avatarId: string;
  avatarPhotoId: string | null;
  /** Verified account → `Avatar` stamps the badge. A bot is never official. */
  official: boolean;
}

/** The real person at a seat, or null for you-with-no-profile / a bot / an unresolved human. */
function humanAt(
  seat: number,
  you: number,
  self: SeatIdentity | null | undefined,
  players: readonly SeatPlayer[] | undefined,
): SeatIdentity | null {
  if (seat === you) return self ?? null;
  return players?.find((p) => p.seat === seat && p.isBot !== true) ?? null;
}

/**
 * The name shown for a seat: your own {@link displayName}, else a known human's, else the game's
 * positional label.
 *
 * `displayName` returns the generic «کاربر» when it has nothing real to work with, and that reads
 * worse at a table than an honest «بات ۲» — so it is treated as no name at all.
 */
export function resolveSeatName(
  seat: number,
  you: number,
  self: SeatIdentity | null | undefined,
  players: readonly SeatPlayer[] | undefined,
  labels: { you: string; fallback: (seat: number) => string },
): string {
  const person = humanAt(seat, you, self, players);
  const name = person ? displayName(person) : "";
  if (name && name !== fa.friends.user) return name;
  return seat === you ? labels.you : labels.fallback(seat);
}

/**
 * The face shown for a seat — the mirror of {@link resolveSeatName}, so a seat's FACE and NAME
 * always come from the same place: a real user's uploaded photo, else THEIR chosen preset, else
 * the game's positional preset.
 */
export function resolveSeatAvatar(
  seat: number,
  you: number,
  self: SeatIdentity | null | undefined,
  players: readonly SeatPlayer[] | undefined,
  presetFor: (seat: number) => string,
): SeatAvatar {
  const person = humanAt(seat, you, self, players);
  return {
    avatarId: person?.avatarId ?? presetFor(seat),
    avatarPhotoId: person?.avatarPhotoId ?? null,
    official: person?.isOfficial === true,
  };
}

/**
 * THE OTHER CHAIR of a table for two, as its board names it: the human the server resolved there,
 * else none — a bot-fill or an unresolved seat, which the board labels itself. ONE mapping for the
 * table and its end card (card-98 review E-M1: two copies had drifted — E-I3).
 */
export function otherHuman(
  players: readonly LobbyPlayer[] | undefined,
  you: number,
): SeatIdentity | null {
  const opp = players?.find((p) => p.seat !== you && !p.isBot);
  return opp
    ? {
        nickname: opp.name,
        avatarId: opp.user?.avatarId,
        avatarPhotoId: opp.user?.avatarPhotoId,
        isOfficial: opp.user?.isOfficial,
      }
    : null;
}
